import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import ReactPlayer from "react-player";
import aboutImage from "../assets/home-images/about.png";
import { useEffect } from "react";
import TableOfContents from "../components/TableOfContents";
import floorplanImage from "../assets/about/floorplan.jpg";
import { Twitter } from "lucide-react";
const images = {
	floorplanImage,
};

const content = `

Hi! I'm Arjun.
<br />
<br />
My love for software grew from the concept of wayfinding in architecture. As a kid, I would map each building or house in my head, obsessing over how different spaces would make me — and especially others — feel.

![Floorplan](floorplanImage)

###### One of my favorite floorplans, Case Study House #5 by Whitney R. Smith, 1945. I particularly like its stripped down, containerized spaces: "You will find no entry, no halls, no breakfast nook. The plan takes order only from its own purpose."

Soon after, I got my first MacBook and began collecting productivity apps like Legos. Today, I see software like virtual floor plans, where every nook and cranny must be meticulously designed to create a space that is both functional and beautiful.


## Early Days
My first gig was at a very early-stage startup called RF Labs. Although I started out as a lowly unpaid intern, it was here that I was exposed to the world of startups for the first time and eagerly worked my way up to a full-time role.

The most important takeaway from my nearly two years there, coming from a design background, was the true role of a designer: to take an idea as far as possible. I got tired of a high-fidelity mockup as far as I could go and simply marking it as 'ready for development', so on the job, I taught myself how to code. I particularly like this relevant quote from [David Hoang](https://www.davidhoang.com/writing/building-your-designs):

<blockquote>
At its core, design is taking an idea and making it tangible. Building your designs is a continuation and extension of that tangibility.
</blockquote>


## Today

This summer, I have the privilege of leading AI efforts on the Automotive Design team at SiriusXM.

I'm also passionate about building:
- at the app layer
- [opinionated software](https://www.figma.com/blog/the-linear-method-opinionated-software/)
- AI-native systems

If you're building any of these things, or just want to chat, I'd love to grab coffee! I'm currently in:
<span className="font-garamond text-2xl">Manhattan, NY</span>

Thanks for visiting my corner of the Internet!

<span className="flex items-center gap-x-2">
<a href="https://x.com/arjundewan_">
	<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-twitter-icon lucide-twitter"><path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"/></svg>
</a>
<a href="https://www.linkedin.com/in/arjunjdewan/"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-linkedin-icon lucide-linkedin"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect width="4" height="12" x="2" y="9"/><circle cx="4" cy="4" r="2"/></svg></a>
</span>
`;

const About = () => {
	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	return (
		<div className="flex flex-col w-full min-h-screen items-center p-4 lg:p-20 gap-y-8 lg:gap-y-20 bg-[#09260B] text-[#F4ECD7]">
			<TableOfContents
				content={content}
				pageName="About"
				accentColor="#F4ECD7"
				darkMode={true}
			/>
			<div className="flex flex-col lg:flex-row header w-full max-w-[1000px] bg-[#88c48c] p-4 lg:p-20 rounded-3xl bg-gradient-to-b from-black/20 via-black/50 to-black/80 shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_-2px_1px_0_rgba(0,0,0,0.08)_inset,0_2px_1px_0_rgba(255,255,255,0.5)_inset,0_2px_5px_-1px_rgba(0,0,0,0.05),0_1px_3px_-1px_rgba(0,0,0,0.3)] border border-accent/20">
				<div className="flex flex-col w-full lg:min-w-[400px] lg:max-w-[400px]">
					<h1 className="text-6xl lg:text-[120px] font-medium mb-4 lg:mt-0 mt-12 text-pretty">
						About
					</h1>
				</div>
				<div className="flex w-full items-center justify-center mt-[-24px]">
					<img
						src={aboutImage}
						alt="hero"
						className="w-full object-contain my-4 lg:my-12 max-h-[300px] lg:max-h-[500px] rounded-2xl mx-auto"
					/>
				</div>
			</div>
			<div className="body w-full pt-8 lg:pt-10 max-w-[1000px] p-4 lg:p-20 rounded-[50px]">
				<Markdown
					rehypePlugins={[rehypeRaw]}
					components={{
						h1: ({ node, ...props }) => (
							<h1 className="text-4xl font-bold my-8 text-center" {...props} />
						),
						h2: ({ node, ...props }) => (
							<h2 className="text-3xl font-bold mb-6 mt-4" {...props} />
						),
						h3: ({ node, ...props }) => (
							<h3 className="text-2xl font-semibold mb-2" {...props} />
						),
						h4: ({ node, ...props }) => (
							<h4
								className="text-2xl font-semibold mb-2 leading-tight"
								{...props}
							/>
						),
						h5: ({ node, ...props }) => (
							<h5 className="text-2xl font-medium mb-2" {...props} />
						),
						h6: ({ node, ...props }) => (
							<h6
								className="text-sm italic text-center mt-[-32px] mb-8"
								{...props}
							/>
						),
						p: ({ node, ...props }) => (
							<p
								className="text-xl mb-4 leading-tight text-[#F4ECD7]"
								{...props}
							/>
						),
						a: ({ node, ...props }) => (
							<a
								className="text-[#88c48c] underline"
								target="_blank"
								rel="noopener noreferrer"
								{...props}
							/>
						),
						ul: ({ node, ...props }) => (
							<ul
								className="list-disc list-inside mb-4 ml-4 text-xl"
								{...props}
							/>
						),
						img: ({ node, ...props }) => {
							const imageSrc = images[props.src] || props.src;
							if (imageSrc.endsWith(".mp4")) {
								return (
									<ReactPlayer
										url={imageSrc}
										controls
										className="my-12 rounded-2xl mx-auto"
									/>
								);
							}
							return (
								<img
									src={imageSrc}
									alt={props.alt}
									className="h-full object-contain my-12 max-h-[500px] rounded-2xl mx-auto"
								/>
							);
						},
						blockquote: ({ node, ...props }) => (
							<blockquote
								className="text-xl mb-4 leading-tight text-[#F4ECD7] pl-4 border-l-4 border-[#88c48c]"
								{...props}
							/>
						),
					}}
				>
					{content}
				</Markdown>
			</div>
		</div>
	);
};

export default About;
